import { formatDate } from '~/prix/types/date'
import type { RuralReportTemplateProps } from '../../report/ruralReportTemplateTitle'

export const equipmentLoanReportSituationValues = ['loaned', 'returned'] as const

export type EquipmentLoanReportSituation = (typeof equipmentLoanReportSituationValues)[number]

export interface EquipmentLoanReportFilters {
  situation?: EquipmentLoanReportSituation | null
  startDate?: Date | string | null
  endDate?: Date | string | null
}

export interface EquipmentLoanReportEntry {
  id: string
  protocolCode: number | null
  situation: EquipmentLoanReportSituation
  loanDate?: Date | string | null
  estimatedDevolutionDate?: Date | string | null
  devolutionDate?: Date | string | null
  citizenId: string
  citizenName: string
  equipmentId: string
  equipmentTitle: string
  equipmentType: string
  equipmentPatrimonyNumber?: string | null
  equipmentPlate?: string | null
  devolutionObservation?: string | null
}

export interface RuralEquipmentLoanReportProperties extends RuralReportTemplateProps {
  filters?: EquipmentLoanReportFilters
  data?: EquipmentLoanReportEntry[]
}

export const equipmentLoanReportTitle = 'Relatório de empréstimos realizados'
export const equipmentLoanReportBaseName = 'equipment-loan-report'
export const equipmentLoanReportColor = '#2B6F8E'

export const equipmentLoanReportSituationLabelMap = {
  loaned: 'Em empréstimo',
  returned: 'Devolvido',
} satisfies Record<EquipmentLoanReportSituation, string>

function extractDateParts(value?: Date | string | null) {
  if (!value) {
    return null
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null
    }

    return {
      day: value.getUTCDate(),
      month: value.getUTCMonth() + 1,
      year: value.getUTCFullYear(),
    }
  }

  const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/)

  if (isoDateMatch) {
    return {
      year: Number(isoDateMatch[1]),
      month: Number(isoDateMatch[2]),
      day: Number(isoDateMatch[3]),
    }
  }

  const parsedDate = new Date(value)

  if (Number.isNaN(parsedDate.getTime())) {
    return null
  }

  return {
    day: parsedDate.getUTCDate(),
    month: parsedDate.getUTCMonth() + 1,
    year: parsedDate.getUTCFullYear(),
  }
}

export function formatEquipmentLoanReportText(value?: string | null, emptyValue = 'Não informado') {
  return value?.trim() || emptyValue
}

export function formatEquipmentLoanReportDate(
  value?: Date | string | null,
  emptyValue = 'Não informado',
) {
  const dateParts = extractDateParts(value)

  if (!dateParts) {
    if (!value) {
      return emptyValue
    }

    return String(value)
  }

  const javascriptDate = new Date(Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day))

  return formatDate(javascriptDate)
}

export function formatEquipmentLoanReportSituation(
  situation?: EquipmentLoanReportSituation | null,
  emptyValue = 'Não informado',
) {
  if (!situation) {
    return emptyValue
  }

  return equipmentLoanReportSituationLabelMap[situation]
}

export function buildEquipmentLoanPeriodLabel(
  startDate?: Date | string | null,
  endDate?: Date | string | null,
  emptyValue = 'Não informado',
) {
  const formattedStartDate = formatEquipmentLoanReportDate(startDate, '')
  const formattedEndDate = formatEquipmentLoanReportDate(endDate, '')

  if (!formattedStartDate && !formattedEndDate) {
    return emptyValue
  }

  if (formattedStartDate && formattedEndDate) {
    if (formattedStartDate === formattedEndDate) {
      return formattedStartDate
    }

    return `${formattedStartDate} até ${formattedEndDate}`
  }

  if (formattedStartDate) {
    return `A partir de ${formattedStartDate}`
  }

  return `Até ${formattedEndDate}`
}
